import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import Button from 'react-bootstrap/Button'
import { Modal } from 'react-bootstrap'
import axios from '../axiosInstance'
import Sidebar from '../components/sidebar'
import Topbar from '../components/topbar'
import Pagination from '../components/Pagination'
import { errorMessage } from '../hooks/UserLogs'
import { formatQueryDate, getToday } from '../utils/date'
import { displayValue, formatDateTime, formatMilliseconds, getApiStatusMeta } from '../utils/format'
import './ApiGroup.css'

const PAGE_LIMIT = 10

export default function ApiRecords () {
  const lng = useSelector((state) => state)
  const navigate = useNavigate()
  const { apiTypeId } = useParams()
  const [searchParams] = useSearchParams()
  const bUrl = process.env.REACT_APP_B_URL

  const dtParam = searchParams.get('dt') || formatQueryDate(getToday())
  const apiNameParam = searchParams.get('apiName') || ''

  const [records, setRecords] = useState([])
  const [totalPage, setTotalPage] = useState(1)
  const [currentPage, setCurrentPage] = useState(1)
  const [isLoading, setIsLoading] = useState(true)
  const [showError, setShowError] = useState(false)
  const [errMsg, setErrMsg] = useState('')

  const axiosInstance = axios.create({ withCredentials: true })

  const fetchRecords = useCallback(
    async (page) => {
      setIsLoading(true)
      const url =
        bUrl +
        '/api-records?apiTypeId=' +
        encodeURIComponent(apiTypeId) +
        '&dt=' +
        encodeURIComponent(dtParam) +
        '&page=' +
        page +
        '&limit=' +
        PAGE_LIMIT

      const response = await axiosInstance.get(url).catch((err) => {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          setErrMsg(lng.UnauthorizedOperation || "Vous n'êtes pas autorisé à faire cette opération.")
          setShowError(true)
          if (err?.response?.status === 401) {
            navigate('/backoffice/login')
          }
        } else {
          setErrMsg(lng.GenericError || "Une erreur s'est produite.")
          setShowError(true)
        }
        setIsLoading(false)
        return null
      })

      if (!response) return

      if (response.status === 401) {
        navigate('/backoffice/login')
        return
      }

      if (response?.data?.isError) {
        const msg = errorMessage(response?.data?.code) || response?.data?.msg || lng.GenericError
        setErrMsg(msg)
        setShowError(true)
        setRecords([])
      } else {
        setRecords(response.data.body || [])
        setTotalPage(response.data.totalPage || 1)
        setCurrentPage(response.data.currentPage || page)
      }
      setIsLoading(false)
    },
    [apiTypeId, bUrl, dtParam, lng.GenericError, lng.UnauthorizedOperation, navigate]
  )

  useEffect(() => {
    if (sessionStorage.getItem('name') === '' || sessionStorage.getItem('name') === null) {
      navigate('/backoffice/login')
      return
    }
    if (!apiTypeId) {
      navigate('/api-group')
      return
    }
    fetchRecords(currentPage)
  }, [apiTypeId, currentPage, dtParam, fetchRecords, navigate])

  const backUrl = '/api-group?dt=' + encodeURIComponent(dtParam)

  const renderApiStatus = (status) => {
    const meta = getApiStatusMeta(status, lng)
    return (
      <span className={meta.className}>
        <span className="list-status-dot" aria-hidden="true" />
        {meta.label}
      </span>
    )
  }

  return (
    <div className="App list-page api-records-page">
      <Sidebar />
      <section className="home">
        <div className="row">
          <div className="d-grid col-12 gap-3">
            <div className="table-content mt-2 p-2 text d-flex justify-content-between align-items-start">
              <div className="list-page-header">
                <p className="fw-bold mb-0">
                  <Link to={backUrl} className="list-breadcrumb-parent">
                    {lng.ApiGroupTitle}
                  </Link>
                  {' > '}
                  {lng.Details}
                </p>
                {(apiNameParam || dtParam) ? (
                  <div className="api-records-meta">
                    {apiNameParam ? <span className="api-name-mono">{apiNameParam}</span> : null}
                    {apiNameParam && dtParam ? ' · ' : null}
                    {dtParam}
                  </div>
                ) : null}
              </div>
              <Topbar />
            </div>

            <div className="d-grid pb-2 gap-3">
              <div className="list-table-wrap p-3">
                {isLoading ? (
                  <div className="loading">
                    <img src="../Custom.svg" alt="" />
                  </div>
                ) : records.length > 0 ? (
                  <>
                    <div className="table-responsive">
                      <table className="table list-table text mb-0">
                        <thead>
                          <tr>
                            <th scope="col">#</th>
                            <th scope="col">{lng.TimeRequest}</th>
                            <th scope="col">{lng.TimeResponse}</th>
                            <th scope="col">{lng.ApiStatus}</th>
                            <th scope="col">{lng.ResponseDuration}</th>
                            <th scope="col">{lng.Message}</th>
                            <th scope="col">{lng.ClientCode}</th>
                            <th scope="col">{lng.CustomerPhone}</th>
                            <th scope="col">{lng.MeterNo}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {records.map((row, index) => (
                            <tr key={(row.idApiRecord?.random || '') + '-' + index}>
                              <td>{(currentPage - 1) * PAGE_LIMIT + index + 1}</td>
                              <td>{formatDateTime(row.timeRequest)}</td>
                              <td>{formatDateTime(row.timeReponse)}</td>
                              <td>{renderApiStatus(row.apiStatus)}</td>
                              <td>{formatMilliseconds(row.timespanMS)}</td>
                              <td>{displayValue(row.msg)}</td>
                              <td>{displayValue(row.customerCode)}</td>
                              <td>{displayValue(row.customerPhone)}</td>
                              <td>{displayValue(row.meterNo)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="table-pagination">
                      <Pagination
                        totalPage={totalPage}
                        setCurrentPage={setCurrentPage}
                        currentPage={currentPage}
                      />
                    </div>
                  </>
                ) : (
                  <div className="list-empty">
                    <i className="bx bx-block" />
                    <p>{lng.NoApiRecords}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Modal show={showError} onHide={() => setShowError(false)}>
        <Modal.Header closeButton className="bg-danger">
          <Modal.Title>{lng.ErrorTitle || 'ERROR'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h5 className="text-danger text-center">{errMsg}</h5>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowError(false)}>
            {lng.Close}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
